import React, { useContext, useEffect, useState } from "react";

import { RemixBuilderContext } from "./RemixBuilderContext";

import CFColorPicker from "../../shared/forms/CFColorPicker";
import CFSelect from "../../shared/forms/CFSelect";
import CFImageUpload from "../../shared/forms/CFImageUpload";
import {
  brandRemixLightColorSettings,
  brandRemixDarkColorSettings,
} from "../../../utils/brandRemixWebsiteTheme";

function normalizeFontValue(val) {
  if (val == null) return { value: "", label: "Theme font" };
  if (typeof val === "string" && val.trim() === "") {
    return { value: "", label: "Theme font" };
  }
  if (typeof val === "object" && "value" in val) {
    const v = val.value;
    if (v == null || String(v).trim() === "") {
      return { value: "", label: "Theme font" };
    }
    return { value: v, label: val.label ?? v };
  }
  if (typeof val === "string") {
    return { value: val, label: val };
  }
  return { value: "", label: "Theme font" };
}

function normalizeStylesFonts(styles) {
  if (!styles || typeof styles !== "object") return styles;
  const next = { ...styles };
  ["heading-font-family", "paragraph-font-family"].forEach((key) => {
    if (next[key] != null) {
      const normalized = normalizeFontValue(next[key]);
      if (normalized) next[key] = normalized;
    }
  });
  return next;
}

const RemixBuilderSettingsTheme = (props) => {
  const { applyThemeOnlyOnClick = false, showBackButton = true } = props;
  const [contextData, builder] = useContext(RemixBuilderContext);

  const [sectionsList, setSectionsList] = useState(
    contextData.matched_sections
  );
  const [loadingNextStep, setLoadingNextStep] = useState(false);
  const [showPresets, setShowPresets] = useState(true);
  const [fontsReady, setFontsReady] = useState(0);
  const [selectedThemeId, setSelectedThemeId] = useState(
    applyThemeOnlyOnClick
      ? undefined
      : contextData.detected_asset_theme
      ? "uploaded-image-theme"
      : undefined
  );

  useEffect(() => {
    setSectionsList(contextData.matched_sections);
  }, [contextData.matched_sections]);

  // Load all font families from all themes
  useEffect(() => {
    const loadAllThemeFonts = () => {
      if (!window.ctas?.builder?.fonts?.load_font_family) return;
      presetThemes.forEach((theme) => {
        if (theme.settings["heading-font-family"]?.value) {
          window.ctas.builder.fonts.load_font_family(
            theme.settings["heading-font-family"].value
          );
        }
        if (theme.settings["paragraph-font-family"]?.value) {
          window.ctas.builder.fonts.load_font_family(
            theme.settings["paragraph-font-family"].value
          );
        }
      });
    };

    loadAllThemeFonts();
  }, []);

  // When in customize view, ensure font dropdown is initialized (e.g. AI/embedded flow) and re-render when loaded
  useEffect(() => {
    if (
      !showPresets &&
      typeof window.ctas?.builder?.fonts?.initialize === "function"
    ) {
      if (!window.builder_fonts_dropdown?.length) {
        window.ctas.builder.fonts.initialize(() => setFontsReady((n) => n + 1));
      }
    }
  }, [showPresets]);

  // Load selected fonts when font inputs are shown (customize view)
  useEffect(() => {
    if (!showPresets && window.ctas?.builder?.fonts?.load_font_family) {
      const headingFont =
        contextData.remix_settings?.styles?.["heading-font-family"]?.value;
      const paragraphFont =
        contextData.remix_settings?.styles?.["paragraph-font-family"]?.value;
      if (headingFont) {
        window.ctas.builder.fonts.load_font_family(headingFont);
      }
      if (paragraphFont) {
        window.ctas.builder.fonts.load_font_family(paragraphFont);
      }
    }
  }, [
    showPresets,
    contextData.remix_settings?.styles?.["heading-font-family"]?.value,
    contextData.remix_settings?.styles?.["paragraph-font-family"]?.value,
    fontsReady,
  ]);

  const handleNextStep = async () => {
    if (loadingNextStep === true) return;

    setLoadingNextStep(true);
    builder.goToNextStep();
  };

  const handlePreviousStep = () => {
    if (loadingNextStep === true) return;

    builder.goToPreviousStep();
  };

  const brandStyleOptions = [
    { value: "dark", label: "Dark Brand Style" },
    { value: "light", label: "Light Brand Style" },
    { value: "custom", label: "Custom Image Upload" },
  ];

  const getBrandTheme = () => {
    const d = contextData.website.data || {};
    const colors = brandRemixLightColorSettings(d);
    const brandHeadlineFont = d["brand_font_headline"];
    const brandParagraphFont = d["brand_font_paragraph"];

    return {
      id: "brand-theme",
      name: "Your Brand",
      description: "Your brand colors and fonts",
      isBrandTheme: true,
      preview: {
        colors: [
          colors["background-color"],
          colors["button-color"],
          colors["accent-color"],
          colors["heading-font-color"],
        ],
        primaryColor: colors["button-color"],
        backgroundColor: colors["background-color"],
      },
      settings: {
        "logo-style": "dark",
        "heading-font-family": brandHeadlineFont
          ? { value: brandHeadlineFont, label: brandHeadlineFont }
          : { value: "Inter", label: "Inter" },
        "paragraph-font-family": brandParagraphFont
          ? { value: brandParagraphFont, label: brandParagraphFont }
          : { value: "Inter", label: "Inter" },
        ...colors,
      },
      headingStyle: "HEADING",
      fontWeight: "900",
    };
  };

  const getBrandThemeDark = () => {
    const d = contextData.website.data || {};
    const colors = brandRemixDarkColorSettings(d);
    const brandHeadlineFont = d["brand_font_headline"];
    const brandParagraphFont = d["brand_font_paragraph"];

    return {
      id: "brand-theme-dark",
      name: "Your Brand Dark",
      description: "Your brand colors and fonts",
      dark: true,
      preview: {
        colors: [
          colors["background-color"],
          colors["button-color"],
          colors["accent-color"],
          colors["heading-font-color"],
        ],
        primaryColor: colors["button-color"],
        backgroundColor: colors["background-color"],
      },
      settings: {
        "logo-style": "light",
        "heading-font-family": brandHeadlineFont
          ? { value: brandHeadlineFont, label: brandHeadlineFont }
          : { value: "Inter", label: "Inter" },
        "paragraph-font-family": brandParagraphFont
          ? { value: brandParagraphFont, label: brandParagraphFont }
          : { value: "Inter", label: "Inter" },
        ...colors,
      },
      headingStyle: "HEADING",
      fontWeight: "700",
    };
  };

  const getUploadedImageTheme = () => {
    const dat = contextData.detected_asset_theme;
    if (!dat) return null;
    const bg = dat["background-color"] || "#ffffff";
    const btn = dat["button-color"] || "#0D42E4";
    const buttonText = dat["button-text-color"] || "#ffffff";
    const accent = dat["accent-color"] || btn;
    const headingColor = dat["heading-font-color"] || "#000000";
    return {
      id: "uploaded-image-theme",
      name: "Uploaded Image",
      description: "Colors detected from your uploaded image",
      isUploadedTheme: true,
      preview: {
        colors: [bg, btn, accent, headingColor],
        primaryColor: btn,
        backgroundColor: bg,
      },
      settings: {
        "logo-style": "dark",
        "heading-font-family": dat["heading-font-family"] || {
          value: "Inter",
          label: "Inter",
        },
        "paragraph-font-family": dat["paragraph-font-family"] || {
          value: "Inter",
          label: "Inter",
        },
        "heading-font-color": headingColor,
        "paragraph-font-color": dat["paragraph-font-color"] || "#000000",
        "button-color": btn,
        "button-text-color": buttonText,
        "accent-color": accent,
        "background-color": bg,
      },
      headingStyle: "HEADING",
      fontWeight: "700",
    };
  };

  const uploadedImageTheme = getUploadedImageTheme();
  const presetThemes = [
    ...(uploadedImageTheme ? [uploadedImageTheme] : []),
    getBrandTheme(),
    getBrandThemeDark(),
    {
      id: "blue",
      name: "Blue",
      description: "Blue theme",
      preview: {
        colors: ["#ffffff", "#d1d5db", "#336EAA", "#000000"],
        primaryColor: "#336EAA",
        backgroundColor: "#ffffff",
      },
      settings: {
        "logo-style": "dark",
        "heading-font-family": { value: "Geologica", label: "Geologica" },
        "paragraph-font-family": { value: "Inter", label: "Inter" },
        "heading-font-color": "#000000",
        "paragraph-font-color": "#000000",
        "button-color": "#336EAA",
        "button-text-color": "#ffffff",
        "accent-color": "#336EAA",
        "background-color": "#ffffff",
      },
      headingStyle: "HEADING",
      fontWeight: "900",
    },
    {
      id: "bold",
      name: "Bold",
      description: "Bold theme",
      preview: {
        colors: ["#F3D1A5", "#ED6C39", "#191A1A", "#000000"],
        primaryColor: "#ED6C39",
        backgroundColor: "#ffffff",
      },
      settings: {
        "logo-style": "dark",
        "heading-font-family": {
          value: "Dela Gothic One",
          label: "Dela Gothic One",
        },
        "paragraph-font-family": { value: "Inter", label: "Inter" },
        "heading-font-color": "#000000",
        "paragraph-font-color": "#000000",
        "button-color": "#ED6C39",
        "button-text-color": "#ffffff",
        "accent-color": "#191A1A",
        "background-color": "#ffffff",
      },
      headingStyle: "Heading",
      fontWeight: "700",
    },
    {
      id: "elegant-serif",
      name: "Elegant Serif",
      description: "Sophisticated serif theme with purple accents",
      preview: {
        colors: ["#F7F8F7", "#707170", "#47DE46", "#0D3D3E"],
        primaryColor: "#0D3D3E",
        backgroundColor: "#F7F8F7",
      },
      settings: {
        "logo-style": "dark",
        "heading-font-family": { value: "Georgia", label: "Georgia" },
        "paragraph-font-family": { value: "DM Sans", label: "DM Sans" },
        "heading-font-color": "#000",
        "paragraph-font-color": "#707170",
        "button-color": "#0D3D3E",
        "button-text-color": "#ffffff",
        "accent-color": "#47DE46",
        "background-color": "#F7F8F7",
      },
      headingStyle: "Heading",
      fontWeight: "400",
    },
  ];

  const handleFontShuffle = () => {
    const currentHeading =
      contextData.remix_settings?.styles?.["heading-font-family"]?.value;
    const currentParagraph =
      contextData.remix_settings?.styles?.["paragraph-font-family"]?.value;

    const georgiaParagraph = { value: "Georgia", label: "Georgia" };
    const headingFonts = presetThemes
      .map((t) => t.settings["heading-font-family"])
      .filter((f) => f?.value && f.value !== currentHeading);
    const paragraphFonts = [
      ...presetThemes
        .map((t) => t.settings["paragraph-font-family"])
        .filter((f) => f?.value && f.value !== currentParagraph),
      ...(currentParagraph !== "Georgia" ? [georgiaParagraph] : []),
    ];

    // Fall back to all fonts if current is the only option
    const headingPool =
      headingFonts.length > 0
        ? headingFonts
        : presetThemes
            .map((t) => t.settings["heading-font-family"])
            .filter((f) => f?.value);
    const allParagraphFonts = [
      ...presetThemes
        .map((t) => t.settings["paragraph-font-family"])
        .filter((f) => f?.value),
      georgiaParagraph,
    ];
    const paragraphPool =
      paragraphFonts.length > 0
        ? paragraphFonts
        : allParagraphFonts.filter((f) => f.value !== currentParagraph);

    if (headingPool.length === 0 || paragraphPool.length === 0) return;

    const heading = headingPool[Math.floor(Math.random() * headingPool.length)];
    const paragraph =
      paragraphPool[Math.floor(Math.random() * paragraphPool.length)];

    builder.updateRemixSettings("styles", {
      ...contextData.remix_settings.styles,
      "heading-font-family": heading,
      "paragraph-font-family": paragraph,
    });
    if (window.ctas?.builder?.fonts?.load_font_family) {
      window.ctas.builder.fonts.load_font_family(heading.value);
      window.ctas.builder.fonts.load_font_family(paragraph.value);
    }
  };

  const handleColorShuffle = () => {
    const colorKeys = [
      "heading-font-color",
      "paragraph-font-color",
      "button-color",
      "button-text-color",
      "accent-color",
      "background-color",
    ];
    const currentColors = colorKeys.reduce((acc, key) => {
      acc[key] = contextData.remix_settings?.styles?.[key];
      return acc;
    }, {});

    const colorPalettes = presetThemes.map((t) =>
      colorKeys.reduce((acc, key) => {
        acc[key] = t.settings[key];
        return acc;
      }, {})
    );

    const differentPalettes = colorPalettes.filter(
      (palette) =>
        !colorKeys.every((key) => palette[key] === currentColors[key])
    );
    const pool =
      differentPalettes.length > 0 ? differentPalettes : colorPalettes;
    const palette = pool[Math.floor(Math.random() * pool.length)];

    builder.updateRemixSettings("styles", {
      ...contextData.remix_settings.styles,
      ...palette,
    });
  };

  const handleThemeSelect = (theme) => {
    // Set selected theme
    setSelectedThemeId(theme.id);

    // Apply all theme settings
    builder.updateRemixSettings("styles", {
      ...contextData.remix_settings.styles,
      ...theme.settings,
    });

    // Load fonts if they're different from current
    if (
      window.ctas?.builder?.fonts?.load_font_family &&
      theme.settings["heading-font-family"]?.value
    ) {
      window.ctas.builder.fonts.load_font_family(
        theme.settings["heading-font-family"].value
      );
    }
    if (
      window.ctas?.builder?.fonts?.load_font_family &&
      theme.settings["paragraph-font-family"]?.value
    ) {
      window.ctas.builder.fonts.load_font_family(
        theme.settings["paragraph-font-family"].value
      );
    }

    setShowPresets(false);
  };

  const LogoSection = () => (
    <>
      <div className="mb-6">
        <div className="mb-[5px] col-xs-9 no-padding-sides">
          <h5 className="text-[18px] font-semibold">Logo</h5>
        </div>

        <CFSelect
          name="logo-style"
          className="smart-select"
          value={
            brandStyleOptions.find(
              (option) =>
                option.value === contextData.remix_settings.styles["logo-style"]
            ) || brandStyleOptions[0]
          }
          options={brandStyleOptions}
          onChange={(selection) => {
            builder.updateRemixSettings("styles", {
              ...contextData.remix_settings.styles,
              "logo-style": selection.value,
            });
          }}
        />
      </div>

      {contextData.remix_settings.styles["logo-style"] === "dark" && (
        <div className="mb-6 min-w-0">
          <CFImageUpload
            name="logo-image"
            value={contextData.website.data?.["logo-dark"] || ""}
            websiteId={contextData.website.id}
            environment={contextData.environment}
            uploadPath={`/websites/${contextData.website.id}/logos/`}
            previewMaxWidth="200px"
            fullWidthButton={true}
            onChange={(value) => {
              builder.updateRemixSettings("styles", {
                ...contextData.remix_settings.styles,
                "logo-image": value,
                "logo-style": "custom",
              });
            }}
            note="Recommended size: 200px by 60px. PNG, JPG, or Webp with transparent background."
          />
        </div>
      )}

      {contextData.remix_settings.styles["logo-style"] === "light" && (
        <div className="mb-6 min-w-0">
          <CFImageUpload
            name="logo-image"
            value={contextData.website.data?.["logo-light"] || ""}
            websiteId={contextData.website.id}
            environment={contextData.environment}
            uploadPath={`/websites/${contextData.website.id}/logos/`}
            previewMaxWidth="200px"
            fullWidthButton={true}
            darkPreview={true}
            onChange={(value) => {
              builder.updateRemixSettings("styles", {
                ...contextData.remix_settings.styles,
                "logo-image": value,
                "logo-style": "custom",
              });
            }}
            note="Recommended size: 200px by 60px. PNG, JPG, or Webp with transparent background."
          />
        </div>
      )}

      {contextData.remix_settings.styles["logo-style"] === "custom" && (
        <div className="mb-6 min-w-0">
          <CFImageUpload
            name="logo-image"
            value={contextData.remix_settings.styles["logo-image"] || ""}
            websiteId={contextData.website.id}
            environment={contextData.environment}
            uploadPath={`/websites/${contextData.website.id}/logos/`}
            previewMaxWidth="200px"
            fullWidthButton={true}
            onChange={(value) => {
              builder.updateRemixSettings("styles", {
                ...contextData.remix_settings.styles,
                "logo-image": value,
              });
            }}
            note="Recommended size: 200px by 60px. PNG, JPG, or Webp with transparent background."
          />
        </div>
      )}
    </>
  );

  const ThemePresetCard = ({ theme, onSelect, isSelected = false }) => (
    <div
      className={`cursor-pointer border-solid border-[1px] rounded-lg overflow-hidden transition-all relative ${
        isSelected ? "border-black" : "border-slate-200 hover:border-black"
      }`}
      onClick={() => onSelect(theme)}
    >
      <>
        {/* Content for non-brand themes (no header) */}
        <div
          className={`p-6 flex items-center justify-between ${
            theme.dark ? "bg-black" : "bg-white"
          }`}
        >
          <div className="flex-1">
            <div
              className="text-[18px] font-semibold mb-1"
              style={{
                fontFamily:
                  theme.settings["heading-font-family"]?.value || "Inter",
                fontWeight: theme.fontWeight || "700",
                color: theme.dark ? "#fff" : "#000",
              }}
            >
              HEADING
            </div>
            <div
              className="text-xl"
              style={{
                fontFamily:
                  theme.settings["paragraph-font-family"]?.value || "Inter",
                color: theme.dark ? "#fff" : "#000",
              }}
            >
              Paragraph
            </div>
          </div>

          <div className="flex items-center space-x-1 ml-4">
            <div
              className="w-8 h-8 rounded-sm"
              style={{ backgroundColor: "#fff", border: "1px solid #f2f2f2" }}
            />
            {theme.preview.colors.map((color, index) => (
              <div
                key={index}
                className="w-8 h-8 rounded-sm"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>
      </>
    </div>
  );

  return (
    <React.Fragment>
      {sectionsList && (
        <>
          <div className="remix-editor-settings-layouts flex flex-column overflow-scroll grow-1 min-w-0">
            {showPresets ? (
              <>
                <h2 className="text-[18px] font-semibold mt-15">
                  Set your theme style
                </h2>
                <p className="text-xl text-gray-400 mb-6">
                  Make your campaign on-brand. Pick from preset themes, or
                  customize the fonts and color palette for your campaign.
                </p>

                <LogoSection />

                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[18px] font-semibold">Themes</h3>
                  {selectedThemeId && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedThemeId(undefined);
                        builder.updateRemixSettings("styles", {});
                      }}
                      className="flex items-center gap-2 px-4 py-2.5 text-xl rounded-lg border-solid border-[1px] border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                        <path d="M3 3v5h5" />
                        <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                        <path d="M16 21h5v-5" />
                      </svg>
                      Reset
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  {presetThemes.map((theme) => (
                    <ThemePresetCard
                      key={theme.id}
                      theme={theme}
                      onSelect={handleThemeSelect}
                      isSelected={selectedThemeId === theme.id}
                    />
                  ))}
                </div>

                <div className="mt-6 pt-4">
                  <div
                    className="cursor-pointer text-center card-new !h-[50px] p-[10px] !pt-[10px] flex items-center justify-center text-gray-700 text-xl"
                    onClick={() => {
                      const customStyles = normalizeStylesFonts(
                        contextData.detected_theme || {}
                      );
                      builder.updateRemixSettings("styles", customStyles);
                      setSelectedThemeId(undefined);
                      setShowPresets(false);
                    }}
                  >
                    Start with custom theme →
                  </div>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-[18px] font-semibold mt-15">
                  Customize your theme style
                </h2>
                <p className="text-xl text-gray-400">
                  Fine-tune the fonts and colors for your template.
                </p>

                {/* Selected Theme Preview */}
                <div className="mt-6 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-[18px] font-semibold">Theme</h5>
                    <div
                      onClick={() => setShowPresets(true)}
                      className="flex items-center text-xl gap-1 text-black cursor-pointer float-right"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="19"
                        height="20"
                        viewBox="0 0 19 20"
                        fill="none"
                      >
                        <path
                          d="M11.4779 14.6921L7.51953 10.7338L11.4779 6.77547"
                          stroke="black"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Back
                    </div>
                  </div>

                  <div className="max-w-md">
                    {(() => {
                      const selectedTheme = presetThemes.find(
                        (theme) => theme.id === selectedThemeId
                      );
                      if (!selectedTheme) return null;

                      return (
                        <ThemePresetCard
                          theme={selectedTheme}
                          onSelect={() => {}}
                          isSelected={true}
                        />
                      );
                    })()}
                  </div>
                </div>

                <div className="mt-6">
                  <LogoSection />
                </div>

                <div className="flex items-center justify-between mb-3 mt-[30px]">
                  <h5 className="text-[18px] font-semibold">Fonts</h5>
                  <button
                    type="button"
                    onClick={handleFontShuffle}
                    className="flex items-center gap-2 px-4 py-2.5 text-xl rounded-lg border-solid border-[1px] border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                      <path d="M3 3v5h5" />
                      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                      <path d="M16 21h5v-5" />
                    </svg>
                    Shuffle
                  </button>
                </div>

                <div className="flex gap-2 mt-5 min-w-0">
                  <div className="flex-1 min-w-0">
                    <div className="mb-[5px] col-xs-9 no-padding-sides">
                      <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">
                        Heading Font
                      </span>
                    </div>

                    <div
                      style={{
                        fontFamily:
                          contextData.remix_settings.styles[
                            "heading-font-family"
                          ]?.value || "inherit",
                      }}
                    >
                      <CFSelect
                        name="heading-font-family"
                        className="smart-select"
                        options={[
                          {
                            value: "",
                            text: "Theme font",
                            group: "System fonts",
                          },
                          ...(window?.builder_fonts_dropdown || []),
                        ]}
                        value={normalizeFontValue(
                          contextData.remix_settings.styles[
                            "heading-font-family"
                          ]
                        )}
                        singleValueClassName="theme-heading-font"
                        onChange={(fontFamily) => {
                          builder.updateRemixSettings("styles", {
                            ...contextData.remix_settings.styles,
                            "heading-font-family": fontFamily
                              ? {
                                  value: fontFamily.value,
                                  label: fontFamily.label ?? fontFamily.value,
                                }
                              : null,
                          });
                          if (
                            fontFamily?.value &&
                            window.ctas?.builder?.fonts?.load_font_family
                          ) {
                            window.ctas.builder.fonts.load_font_family(
                              fontFamily.value
                            );
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-5 min-w-0">
                  <div className="flex-1 min-w-0">
                    <div className="mb-[5px] col-xs-9 no-padding-sides">
                      <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">
                        Paragraph Font
                      </span>
                    </div>

                    <div
                      style={{
                        fontFamily:
                          contextData.remix_settings.styles[
                            "paragraph-font-family"
                          ]?.value || "inherit",
                      }}
                    >
                      <CFSelect
                        name="paragraph-font-family"
                        className="smart-select"
                        options={[
                          {
                            value: "",
                            text: "Theme font",
                            group: "System fonts",
                          },
                          ...(window?.builder_fonts_dropdown || []),
                        ]}
                        value={normalizeFontValue(
                          contextData.remix_settings.styles[
                            "paragraph-font-family"
                          ]
                        )}
                        singleValueClassName="theme-paragraph-font"
                        onChange={(fontFamily) => {
                          builder.updateRemixSettings("styles", {
                            ...contextData.remix_settings.styles,
                            "paragraph-font-family": fontFamily
                              ? {
                                  value: fontFamily.value,
                                  label: fontFamily.label ?? fontFamily.value,
                                }
                              : null,
                          });
                          if (
                            fontFamily?.value &&
                            window.ctas?.builder?.fonts?.load_font_family
                          ) {
                            window.ctas.builder.fonts.load_font_family(
                              fontFamily.value
                            );
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-3 mt-[30px]">
                  <h5 className="text-[18px] font-semibold">Colors</h5>
                  <button
                    type="button"
                    onClick={handleColorShuffle}
                    className="flex items-center gap-2 px-4 py-2.5 text-xl rounded-lg border-solid border-[1px] border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                      <path d="M3 3v5h5" />
                      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                      <path d="M16 21h5v-5" />
                    </svg>
                    Shuffle
                  </button>
                </div>

                <div className="mt-3 mb-4">
                  <div
                    className="rounded-xl border-solid border-[1px] border-slate-200 p-[10px] bg-white"
                    role="img"
                    aria-label="Color theme preview"
                  >
                    <div className="flex gap-0.5 overflow-hidden rounded-lg">
                      {[
                        "background-color",
                        "heading-font-color",
                        "paragraph-font-color",
                        "button-color",
                        "button-text-color",
                        "accent-color",
                      ].map((key) => (
                        <div
                          key={key}
                          className="flex-1 h-12 min-w-[24px]"
                          style={{
                            backgroundColor:
                              contextData.remix_settings?.styles?.[key] ||
                              "#e5e7eb",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-5 min-w-0">
                  <div className="flex-1 min-w-0">
                    <div className="mb-[5px] col-xs-9 no-padding-sides">
                      <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">
                        Heading Color
                      </span>
                    </div>

                    <CFColorPicker
                      name="heading-font-color"
                      placeholder="Select"
                      website={contextData.website}
                      value={
                        contextData.remix_settings.styles["heading-font-color"]
                      }
                      onChange={(value) => {
                        builder.updateRemixSettings("styles", {
                          ...contextData.remix_settings.styles,
                          "heading-font-color": value,
                        });
                      }}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="mb-[5px] col-xs-9 no-padding-sides">
                      <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">
                        Paragraph Color
                      </span>
                    </div>

                    <CFColorPicker
                      name="paragraph-font-color"
                      placeholder="Select"
                      website={contextData.website}
                      value={
                        contextData.remix_settings.styles[
                          "paragraph-font-color"
                        ]
                      }
                      onChange={(value) => {
                        builder.updateRemixSettings("styles", {
                          ...contextData.remix_settings.styles,
                          "paragraph-font-color": value,
                        });
                      }}
                    />
                  </div>
                </div>

                <div className="flex gap-1 mt-5 min-w-0">
                  <div className="flex-1 min-w-0">
                    <div className="mb-[5px] col-xs-9 no-padding-sides">
                      <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">
                        Buttons
                      </span>
                    </div>

                    <CFColorPicker
                      name="button-color"
                      placeholder="Select"
                      website={contextData.website}
                      value={contextData.remix_settings.styles["button-color"]}
                      onChange={(value) => {
                        builder.updateRemixSettings("styles", {
                          ...contextData.remix_settings.styles,
                          "button-color": value,
                        });
                      }}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="mb-[5px] col-xs-9 no-padding-sides">
                      <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">
                        Button Text
                      </span>
                    </div>

                    <CFColorPicker
                      name="button-text-color"
                      placeholder="Select"
                      website={contextData.website}
                      value={
                        contextData.remix_settings.styles["button-text-color"]
                      }
                      onChange={(value) => {
                        builder.updateRemixSettings("styles", {
                          ...contextData.remix_settings.styles,
                          "button-text-color": value,
                        });
                      }}
                    />
                  </div>
                </div>

                <div className="flex gap-1 mt-5 min-w-0">
                  <div className="flex-1 min-w-0">
                    <div className="mb-[5px] col-xs-9 no-padding-sides">
                      <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">
                        Accents
                      </span>
                    </div>

                    <CFColorPicker
                      name="accent-color"
                      placeholder="Select"
                      website={contextData.website}
                      value={contextData.remix_settings.styles["accent-color"]}
                      onChange={(value) => {
                        builder.updateRemixSettings("styles", {
                          ...contextData.remix_settings.styles,
                          "accent-color": value,
                        });
                      }}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="mb-[5px] col-xs-9 no-padding-sides">
                      <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">
                        Background
                      </span>
                    </div>

                    <CFColorPicker
                      name="background-color"
                      placeholder="Select"
                      website={contextData.website}
                      value={
                        contextData.remix_settings.styles["background-color"]
                      }
                      onChange={(value) => {
                        builder.updateRemixSettings("styles", {
                          ...contextData.remix_settings.styles,
                          "background-color": value,
                        });
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="remix-editor-settings-buttons pt-[30px] pb-[30px] gap-4 flex justify-between">
            {showBackButton && (
              <div
                onClick={handlePreviousStep}
                className={`cursor-pointer rounded-xl w-full p-[10px] flex justify-center items-center text-xl border border-solid border-slate-200 bg-white text-black ${
                  loadingNextStep ? "disabled" : ""
                }`}
              >
                Back
              </div>
            )}
            <div
              onClick={handleNextStep}
              className={`cursor-pointer rounded-xl w-full p-[10px] flex justify-center items-center text-xl border border-solid border-black bg-black text-white ${
                loadingNextStep ? "disabled" : ""
              } ${!showBackButton ? "w-full" : ""}`}
            >
              {loadingNextStep ? (
                <span className="cf-loading-spinner" />
              ) : (
                "Continue →"
              )}
            </div>
          </div>
        </>
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
        .theme-heading-font div {
          font-family: ${
            contextData.remix_settings.styles["heading-font-family"]?.value ||
            "inherit"
          };
        }

        .theme-paragraph-font div {
          font-family: ${
            contextData.remix_settings.styles["paragraph-font-family"]?.value ||
            "inherit"
          };
        }
      `,
        }}
      />
    </React.Fragment>
  );
};

export default RemixBuilderSettingsTheme;
